import ClassManagment from '@assets/icons/admin-session/class-management';
import DownloadIcon from '@assets/icons/admin-session/download-icon';
import ExitAdmin from '@assets/icons/admin-session/exit-admin';
import Faq from '@assets/icons/admin-session/faq';
import FileSearch from '@assets/icons/admin-session/file-search';
import MessageManagement from '@assets/icons/admin-session/messages-management';
import Package from '@assets/icons/admin-session/package';
import ReportIcon from '@assets/icons/admin-session/report-icon';
import UserManagementIcon from '@assets/icons/admin-session/user-management';
import ChevronDown2 from '@assets/icons/chevron-down-2';
import ChevronUp2 from '@assets/icons/chevron-up-2';
import NavLogo from '@components/Header/components/nav-logo';
import { Panel } from '@devstart/react-bootstrap';
import { User } from '@redux/prop-types';
import { getInactiveUsers, getNationalDepartmentMetrics } from '@utils/ajax';
import { PermissionLevel } from '@utils/enums/permission-levels';
import { navigate } from 'gatsby-link';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import DarkModeButton from '@components/DarkModeButton';
import {
  LinkMapProps,
  SubitemSectionProps
} from '../../../components/Header/components/nav-links';

import '../../../components/Header/components/nav-links.css';
import '../show-admin-session.css';

interface SidebarProps {
  readonly user: User;
}

interface SubitemSectionFullProps extends SubitemSectionProps {
  currentPath: string;
  isOpen: boolean;
  toggleSection: (key: string) => void;
}

const SubitemSection = ({
  key,
  title,
  icon,
  subitems,
  currentPath,
  isOpen,
  toggleSection
}: SubitemSectionFullProps) => {
  return (
    <Panel eventKey='1' bsClass='custom-accordion-panel' expanded={isOpen}>
      <Panel.Title
        key={key}
        toggle
        className='nav-item'
        onClick={() => toggleSection(title)}
      >
        {icon}
        <span className='nav-link'>{title}</span>
        {isOpen ? (
          <ChevronUp2 className='accordion-chevron' />
        ) : (
          <ChevronDown2 className='accordion-chevron' />
        )}
      </Panel.Title>

      <Panel.Body collapsible>
        {subitems?.map(subitem => (
          <button
            key={subitem.title}
            className={`nav-subitem ${
              subitem.path === currentPath ? 'is-selected' : ''
            }`}
            onClick={subitem.onClick}
          >
            <span
              className={`nav-link ${
                subitem.underline ? 'link-underline' : ''
              }`}
            >
              {subitem.title}
            </span>
            {subitem.icon}
          </button>
        ))}
      </Panel.Body>
    </Panel>
  );
};

interface SidebarProps {
  readonly user: User;
}

const Sidebar: React.FC<SidebarProps> = ({ user }) => {
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({});

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const currentPath = window.location.pathname;

  const handleNavigate = (location: string) => {
    navigate(location) as unknown as void;
  };

  const rawLinkMap: LinkMapProps[] = [
    // Renomeado para rawLinkMap
    {
      title: t('admin-session.titles.user-management'),
      icon: <UserManagementIcon />,
      subitems: [
        {
          title: t('admin-session.titles.pre-enrolled'),
          path: '/admin/pre-enrolled',
          onClick: () => handleNavigate('/admin/pre-enrolled')
        },
        {
          title: 'Lista de espera',
          path: '/admin/waiting-list',
          onClick: () => handleNavigate('/admin/waiting-list')
        },
        {
          title: t('admin-session.titles.enrollment-users'),
          path: '/admin/enrollment-users',
          onClick: () => handleNavigate('/admin/enrollment-users')
        }
      ],
      allowedRoles: [
        PermissionLevel.SUPER,
        PermissionLevel.ADMIN,
        PermissionLevel.SUPPORT
      ]
    },
    {
      title: t('admin-session.titles.class-groups'),
      icon: <ClassManagment />,
      path: '/admin/class-groups',
      onClick: () => handleNavigate('/admin/class-groups'),
      allowedRoles: [
        PermissionLevel.SUPER,
        PermissionLevel.ADMIN,
        PermissionLevel.SUPPORT
      ]
    },
    {
      title: t('admin-session.titles.messages'),
      icon: <MessageManagement />,
      subitems: [
        {
          title: t('admin-session.titles.messages-management'),
          path: '/admin/messages-managment',
          onClick: () => handleNavigate('/admin/messages-managment')
        },
        {
          title: t('admin-session.titles.send-emails'),
          path: '/admin/emails',
          onClick: () => handleNavigate('/admin/emails')
        },
        {
          title: 'Painel de cotas',
          path: '/admin/email-quota-dashboard',
          onClick: () => handleNavigate('/admin/email-quota-dashboard')
        }
      ],
      allowedRoles: [
        PermissionLevel.SUPER,
        PermissionLevel.ADMIN,
        PermissionLevel.SUPPORT
      ]
    },
    {
      title: t('admin-session.titles.logs'),
      icon: <FileSearch />,
      subitems: [
        {
          title: 'Registros de exclusão',
          path: '/admin/delete-register',
          onClick: () => handleNavigate('/admin/delete-register')
        },
        {
          title: 'Integração com Discord',
          path: '/admin/discord-logs',
          onClick: () => handleNavigate('/admin/discord-logs')
        }
      ],
      allowedRoles: [
        PermissionLevel.SUPER,
        PermissionLevel.ADMIN,
        PermissionLevel.SUPPORT
      ]
    },
    {
      title: t('admin-session.titles.reports'),
      icon: <ReportIcon />,
      subitems: [
        {
          title: t('admin-session.titles.national-department-metrics'),
          onClick: () =>
            void getNationalDepartmentMetrics().catch(error =>
              console.error('Error:', error)
            ),
          icon: <DownloadIcon />,
          underline: true
        },
        {
          title: t('admin-session.titles.inactive-users'),
          onClick: () =>
            void getInactiveUsers().catch(error =>
              console.error('Error:', error)
            ),
          icon: <DownloadIcon />,
          underline: true
        }
      ],
      allowedRoles: [
        PermissionLevel.SUPER,
        PermissionLevel.ADMIN,
        PermissionLevel.SUPPORT
      ]
    },
    {
      title: t('admin-session.titles.products'),
      icon: <Package />,
      subitems: [
        {
          title: t('admin-session.titles.link-products'),
          path: '/admin/products',
          onClick: () => handleNavigate('/admin/products')
        }
      ],
      allowedRoles: [PermissionLevel.SUPER, PermissionLevel.ADMIN]
    },
    {
      title: 'Gerenciar FAQ',
      icon: <Faq />,
      path: '/admin/faq',
      onClick: () => handleNavigate('/admin/faq'),
      allowedRoles: [PermissionLevel.SUPER, PermissionLevel.ADMIN]
    }
  ];

  // Filtra a lista de links com base no tipo de usuário
  const linkMap = rawLinkMap.filter(item => {
    if (item.title === t('admin-session.titles.class-groups')) {
      return (
        user.type === 'admin' ||
        user.type === 'super' ||
        user.type === 'support'
      );
    }
    return true; // Mantém todos os outros itens
  });

  useEffect(() => {
    const currentExpandedLink = linkMap.find(item =>
      item?.subitems?.some(subitem => subitem.path === currentPath)
    );
    if (currentExpandedLink) {
      setExpandedSections(prev => ({
        ...prev,
        [currentExpandedLink.title]: true
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <aside className='sidebar'>
      <NavLogo />
      <div className='items-section'>
        {linkMap.map(
          ({ title, icon, subitems, onClick, allowedRoles, path }) => {
            if (subitems && subitems.length > 0) {
              if (allowedRoles && !allowedRoles.includes(user.type)) {
                return null;
              }

              return (
                <SubitemSection
                  key={title}
                  title={title}
                  icon={icon}
                  subitems={subitems}
                  currentPath={currentPath}
                  isOpen={
                    expandedSections[title] ??
                    subitems.some(item => item.path === currentPath) ??
                    false
                  }
                  toggleSection={toggleSection}
                />
              );
            }

            return (
              <button
                key={title}
                className={`nav-item ${
                  path && path === currentPath ? 'is-selected' : ''
                }`}
                onClick={onClick}
              >
                {icon}
                <span className='nav-link'>{title}</span>
              </button>
            );
          }
        )}
      </div>
      <div className='exit-admin'>
        <DarkModeButton />
        <button
          className='nav-item exit-admin'
          onClick={() => handleNavigate('/learn')}
        >
          <ExitAdmin />
          {t('admin-session.titles.exit-admin')}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
